
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import { FormFooter, TagerFormSubmitEvent, FormField } from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { ProductGroupType } from "@/typings/model";
import {
  createProductGroup,
  getProductGroup,
  updateProductGroup,
} from "@/services/requests";
import {
  getCollectionFormUrl,
  getCollectionListUrl,
  getProductGroupFormUrl,
  getProductGroupListUrl,
} from "@/utils/paths";
import { convertCollectionToFormValues } from "@/views/CollectionForm/CollectionForm.helpers";

import {
  convertFormValuesToProductGroupCreationPayload,
  convertFormValuesToProductGroupUpdatePayload,
  convertProductGroupToFormValues,
  FormValues,
} from "./ProductGroupForm.helpers";

export default defineComponent({
  name: "ProductGroupForm",
  components: { FormFooter, FormField, Page },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const productGroupId = computed<string>(
      () => route.params.productGroupId as string
    );

    const isCreation = computed<boolean>(
      () => productGroupId.value === "create"
    );

    const [
      fetchProductGroup,
      { data: productGroup, loading: isProductGroupLoading },
    ] = useResource<Nullable<ProductGroupType>>({
      fetchResource: () => getProductGroup(productGroupId.value),
      initialValue: null,
      resourceName: "productGroup",
    });

    onMounted(() => {
      if (isCreation.value) return;

      fetchProductGroup();
    });

    const values = ref<FormValues>(
      convertProductGroupToFormValues(productGroup.value)
    );

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(productGroup, () => {
      values.value = convertProductGroupToFormValues(productGroup.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToProductGroupCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToProductGroupUpdatePayload(
        values.value
      );
      const requestPromise = isCreation.value
        ? createProductGroup(creationBody)
        : updateProductGroup(productGroupId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(
              getProductGroupFormUrl({ productGroupId: response.data.id })
            );
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getProductGroupListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertProductGroupToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Группа успешно создан`
              : "Группа успешно обновлен",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при создании группы`
              : "Ошибка при обновлении группы",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () => isProductGroupLoading.value
    );

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Создать группу товара`
        : `Редактирование группы товара "${values.value.name}"`;
    });

    return {
      backButtonUrl: getProductGroupListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      productGroup,
      isCreation,
      pageTitle,
    };
  },
});
